"use client";
import { Check } from "lucide-react";
import dynamic from "next/dynamic";
import Image from "next/image"; // Importing the Image component from Next.js
import DomPurify from "isomorphic-dompurify";
import MaxWidthWrapper from "components/Tiles/commonComponents/MaxWidthWrapper";
import DualButtonCTA, { ButtonVariant } from "components/ui/DualButtonCTA";
import { FadeIn } from "components/ui/FadeIn";
import { Badge } from "components/ui/badge";
const MuxPlayer = dynamic(() => import("components/Tiles/commonComponents/MuxPlayer"));
interface ButtonProps {
  buttonText: string;
  buttonUrl: string;
  buttonVariant: ButtonVariant;
}

type ListsTwoProps = {
  miniTitle: string;
  title?: string;
  subTitle?: string;
  bgColor?: string;
  cards?: {
    title: string;
    subTitle: string;
    content: string;
    image?: string;
    videoId?: string;
    listTitle?: string;
    listItems?: {
      title?: string;
    }[];
    button1?: ButtonProps;
    button2?: ButtonProps;
  }[];
  useGifThumbnail?: boolean;
};

const ListsTwo = ({ miniTitle, title, subTitle, bgColor, cards, useGifThumbnail }: ListsTwoProps) => {
  return (
    <section id="ListsTwo-115" style={{ backgroundColor: bgColor }} className="">
      <MaxWidthWrapper className="my-0 px-8  py-0">
        <div className="lg mx-auto w-full">
          <div className="grid grid-cols-2">
            <div className="grid grid-cols-2">
              <div className=" h-24 w-full  rounded-bl-[150px] border-b-[1px] border-l-[1px]  lg:border-l-[1px] lg:border-dashed lg:border-gray-500 lg:pb-24"></div>
              <div className=" h-24 w-full self-center rounded-tr-[150px]  border-r-[1px] border-t-[1px] lg:mt-24 lg:border-dashed lg:border-gray-500"></div>
            </div>
            <div className="col-span-2">
              {miniTitle && (
                <>
                  <div className="mx-auto mb-12 flex h-10 w-10 items-center justify-center rounded-full bg-[#fff] text-[#004733]  lg:h-16 lg:w-16">
                    <span className="text-[20px] font-normal text-[#222]">{miniTitle}</span>
                  </div>
                </>
              )}
              <FadeIn>
                <h3 className="py-6 text-center font-heading text-[30px] leading-[1.2] lg:-mt-2 lg:mb-0 lg:py-0 lg:text-[33px]">
                  {title}
                </h3>
              </FadeIn>
              {subTitle && (
                <p className="mx-auto mt-4 max-w-[700px] text-center text-2xl text-primary-700">{subTitle}</p>
              )}
              <div className="mx-auto max-w-[1200px]">
                <div className="mt-24 grid grid-cols-1 gap-4 rounded-2xl  lg:grid-cols-2 lg:gap-12">
                  {cards &&
                    cards.map((card, index) => (
                      <div key={index} className="flex flex-col gap-6 rounded-xl  bg-[#fffdf6] pb-8 text-left">
                        {card.videoId ? (
                          <div className="relative h-[250px] min-h-[250px]">
                            <MuxPlayer
                              playbackId={card.videoId}
                              loop
                              muted
                              maxResolution="2160p"
                              autoPlay
                              useGifThumbnail={useGifThumbnail}
                              className="border-primary-[#E8ECED] z-10  h-full w-full overflow-hidden rounded-xl rounded-b-none border object-cover"
                            />
                          </div>
                        ) : card.image ? (
                          <div className="relative h-[250px] min-h-[250px]">
                            <Image
                              alt={title || ""}
                              src={card.image}
                              className="border-primary-[#E8ECED] z-10  h-full w-full overflow-hidden rounded-xl rounded-b-none border object-cover"
                              quality={70}
                              style={{
                                objectFit: "cover"
                              }}
                              sizes="(max-width: 942px) 100vw, 50vw"
                              fill
                            />
                          </div>
                        ) : null}
                        <div className="px-8 pb-0">
                          <Badge variant="secondary" className="px-1.5 py-[.5px]">
                            {card.subTitle}
                          </Badge>
                          <div className="mt-6">
                            <h3 className=" pb-2 font-heading text-[22px] leading-[1.2] [text-wrap:balance] lg:-mt-2 lg:mb-0 lg:pb-4 lg:text-[26px]">
                              {card.title}
                            </h3>
                            <p
                              className="text-md font-medium leading-[1.5] text-primary-700"
                              dangerouslySetInnerHTML={{
                                __html: DomPurify.sanitize(card.content || "")
                              }}
                            />
                          </div>

                          <div className="mx-auto mt-4 pb-6  ">
                            {card.listTitle && (
                              <p className="pb-2 pt-6 text-base text-primary-700"> {card.listTitle}</p>
                            )}
                            <ul className="max-w-[85%]  space-y-3 py-[12px] text-left text-base leading-6 text-primary-700  ">
                              {card.listItems?.map((item, idx) => (
                                <li key={idx}>
                                  <FadeIn className="flex gap-x-4 overflow-hidden">
                                    <span className="mt-1 flex h-6 w-6 shrink-0 items-center  justify-center  rounded-full bg-[#073937] [text-wrap:balance]">
                                      <Check color="#fff" className="shrink-0" strokeWidth={2.2} size={17} />
                                    </span>
                                    {item.title}
                                  </FadeIn>
                                </li>
                              ))}
                            </ul>
                          </div>
                          {(card.button1 || card.button2) && (
                            <FadeIn>
                              <DualButtonCTA button1={card.button1} button2={card.button2} isLeftAligned={true} />
                            </FadeIn>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </MaxWidthWrapper>
    </section>
  );
};

export default ListsTwo;
